import type { AlternativeMode } from '@pomoshnik/shared';

/**
 * Секция «Альтернативы» на PaintCard (PRD §6 Block 3 Phase 4) — 4 mood-кнопки
 * в **вертикальном списке** (P-019 MUST). Симметрична с decor-catalog
 * AlternativesSection, но **без `enabled` prop**.
 *
 * Phase 4 open question §4: Paint 100% имеет dominantLab (PAINT_FIXTURE
 * Phase 1 stat «100% coverage»), поэтому disabled state mood-кнопок не
 * нужен. Если в будущем добавятся Tikkurila / Caparol SKU без LAB —
 * восстановим `enabled` prop по образцу DecorCard B-022.
 *
 * Иконки — Unicode glyphs (без lucide-react). Цветные акценты warmer/cooler
 * разрешены design-system v0.2.2.
 */
export interface AlternativesSectionProps {
  onSelect: (mode: AlternativeMode) => void;
}

interface MoodOption {
  mode: AlternativeMode;
  label: string;
  /** Decorative Unicode glyph; aria-hidden — semantic info в label. */
  icon: string;
  /** Опциональный цветной акцент для иконки (warmer / cooler). */
  iconColor?: string;
}

const MOODS: readonly MoodOption[] = [
  { mode: 'lighter', label: 'Светлее', icon: '☀' },
  { mode: 'darker', label: 'Темнее', icon: '🌙' },
  { mode: 'warmer', label: 'Теплее', icon: '🔥', iconColor: '#d97706' },
  { mode: 'cooler', label: 'Холоднее', icon: '❄', iconColor: '#2563eb' },
] as const;

export function AlternativesSection({ onSelect }: AlternativesSectionProps) {
  return (
    <div className="flex flex-col gap-3">
      {MOODS.map((m) => (
        <button
          key={m.mode}
          type="button"
          aria-label={m.label}
          onClick={() => onSelect(m.mode)}
          className={[
            'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
            'text-[16px] py-3 px-4 flex items-center gap-3',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none',
            'active:opacity-90 cursor-pointer',
          ].join(' ')}
        >
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-icon-bg shrink-0"
            aria-hidden="true"
            style={m.iconColor ? { color: m.iconColor } : undefined}
          >
            {m.icon}
          </span>
          <span className="flex-1 text-left">{m.label}</span>
          <span aria-hidden="true" className="text-muted">
            ›
          </span>
        </button>
      ))}
    </div>
  );
}
